import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  CircularProgress,
  Alert
} from '@mui/material';
import { Cancel as CancelIcon } from '@mui/icons-material';
import type { Appointment } from '@app-types/reception';

interface CancelAppointmentModalProps {
  open: boolean;
  appointment: Appointment | null;
  onClose: () => void;
  onConfirm: (appointmentId: string, reason?: string) => Promise<void>;
}

export const CancelAppointmentModal: React.FC<CancelAppointmentModalProps> = ({
  open,
  appointment,
  onClose,
  onConfirm
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const handleConfirm = async () => {
    if (!appointment) return;

    setLoading(true);
    setError(null);

    try {
      await onConfirm(appointment.id, reason || undefined);
      onClose();
      setReason(''); // Reset reason
    } catch (err: any) {
      setError(err.message || 'Eroare la anularea programarii');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setError(null);
    onClose();
  };

  if (!appointment) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CancelIcon color="error" />
          Anulare programare
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body1" gutterBottom>
          Confirmati anularea programarii pentru:
        </Typography>

        <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mt: 2, mb: 2 }}>
          <Typography variant="body2">
            <strong>Animal:</strong> {appointment.animal?.name || 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>Proprietar:</strong>{' '}
            {appointment.owner
              ? `${appointment.owner.firstName} ${appointment.owner.lastName}`
              : 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>Medic:</strong>{' '}
            {appointment.doctor
              ? `Dr. ${appointment.doctor.firstName} ${appointment.doctor.lastName}`
              : 'N/A'}
          </Typography>
        </Box>

        <TextField
          label="Motiv anulare (optional)"
          multiline
          rows={3}
          fullWidth
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Ex: Neprezentare client, programare gresita, etc."
        />

        <Alert severity="warning" sx={{ mt: 2 }}>
          Proprietarul si medicul vor fi notificati despre anulare.
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Inapoi
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CancelIcon />}
        >
          {loading ? 'Se proceseaza...' : 'Anuleaza programarea'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

